/**
 * @file Firebase
 * @module store/firebase
 * @desc Configures Firebase and oversees its usage
 * @since v4.12.0-prod...
 * @backport v5.9.3-beta.20240827.e9e4b63
 */

/**
 * @typedef {import('firebase/app-check').AppCheck} AppCheck
 */

import { initializeApp } from 'firebase/app'
import { getAnalytics, isSupported, logEvent, setUserId, setUserProperties } from 'firebase/analytics'
import { getToken, initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check'
import { fetchAndActivate, getRemoteConfig, getValue } from 'firebase/remote-config'
import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getAuth,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  linkWithCredential,
  onAuthStateChanged,
  reauthenticateWithCredential,
  SAMLAuthProvider,
  sendEmailVerification,
  sendSignInLinkToEmail,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  updatePassword,
} from 'firebase/auth'

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  initializeFirestore,
  limit,
  onSnapshot,
  orderBy,
  persistentLocalCache,
  persistentMultipleTabManager,
  query,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore'

const logger = console

const {
  NODE_ENV,
  VUE_APP_FIREBASE_STORAGE: FIREBASE_STORAGE,
  VUE_APP_FIREBASE_API_KEY: FIREBASE_API_KEY,
  VUE_APP_FIREBASE_APP_ID: FIREBASE_APP_ID,
  VUE_APP_FIREBASE_DATABASE_URL: FIREBASE_DATABASE_URL,
  VUE_APP_FIREBASE_MEASUREMENT_ID: FIREBASE_MEASUREMENT_ID,
  VUE_APP_FIREBASE_MESSAGING_SENDER_ID: FIREBASE_MESSAGING_SENDER_ID,
  VUE_APP_FIREBASE_SCOPE: FIREBASE_SCOPE,
  VUE_APP_GOOGLE_CLIENT_ID: GOOGLE_CLIENT_ID,
  VUE_APP_GOOGLE_PROJECT: GOOGLE_PROJECT,

  // Firebase App Check
  VUE_APP_FIREBASE_APP_CHECK_DEBUG_TOKEN: FIREBASE_APP_CHECK_DEBUG_TOKEN,
  VUE_APP_RECAPTCHA_ENTERPRISE_SITE_KEY: RECAPTCHA_ENTERPRISE_SITE_KEY,

  // Firebase Auth
  VUE_APP_FIREBASE_AUTH_DOMAIN: FIREBASE_AUTH_DOMAIN,

  // Domain & Tenant IDs
  VUE_APP_DOMAIN_NAME,
  VUE_APP_TENANT_ID,
  VUE_APP_DOMAIN_NAME_FOR_ADOPS_TENANT: DOMAIN_NAME_FOR_ADOPS_TENANT,
  VUE_APP_TENANT_ID_FOR_ADOPS_TENANT: TENANT_ID_FOR_ADOPS_TENANT,
  VUE_APP_DOMAIN_NAME_FOR_DEFAULT_TENANT: DOMAIN_NAME_FOR_DEFAULT_TENANT,
  VUE_APP_TENANT_ID_FOR_DEFAULT_TENANT: TENANT_ID_FOR_DEFAULT_TENANT,
} = process.env

export const FIREBASE_DOMAIN_NAME = VUE_APP_DOMAIN_NAME
export const FIREBASE_TENANT_ID = VUE_APP_TENANT_ID

export const FIREBASE_DOMAIN_NAME_FOR_DEFAULT_TENANT = DOMAIN_NAME_FOR_DEFAULT_TENANT
export const FIREBASE_TENANT_ID_FOR_DEFAULT_TENANT = TENANT_ID_FOR_DEFAULT_TENANT

export const FIREBASE_DOMAIN_NAME_FOR_ADOPS_TENANT = DOMAIN_NAME_FOR_ADOPS_TENANT
export const FIREBASE_TENANT_ID_FOR_ADOPS_TENANT = TENANT_ID_FOR_ADOPS_TENANT

const IS_ENV_DEV = /dev/.test(NODE_ENV)
const IS_ENV_UAT = /uat/.test(GOOGLE_PROJECT)

const FIREBASE_CONFIG = {
  apiKey: FIREBASE_API_KEY,
  appId: FIREBASE_APP_ID,
  authDomain: FIREBASE_AUTH_DOMAIN,
  clientId: GOOGLE_CLIENT_ID,
  databaseURL: FIREBASE_DATABASE_URL,
  projectId: GOOGLE_PROJECT,
  measurementId: FIREBASE_MEASUREMENT_ID,
  messagingSenderId: FIREBASE_MESSAGING_SENDER_ID,
  scopes: FIREBASE_SCOPE,
  storageBucket: FIREBASE_STORAGE,
}

// Initialize Firebase & export it.
const firebaseApp = initializeApp(FIREBASE_CONFIG)

/**
 * @function getFireAppCheck
 * @desc Create a Firebase App Check instance with the given Firebase
 * app instance. Gracefully handles initialization errors when App Check
 * is not yet fully configured in Firebase Console.
 * @param {FirebaseApp} app The Firebase app instance.
 * @return {AppCheck} The Firebase App Check instance.
 * @see https://firebase.google.com/docs/app-check/web/recaptcha-provider
 */
export const getFireAppCheck = (app) => {
  try {
    if (IS_ENV_DEV) {
      logger.log('Environment detected: development.')

      if (!FIREBASE_APP_CHECK_DEBUG_TOKEN) {
        logger.warn('FIREBASE_APP_CHECK_DEBUG_TOKEN is not set. App Check may not function correctly in development.')
      } else {
        // Configure Firebase App Check with a debug token for local development.
        // Set to string to support multi-browser and multi-device dev sessions.
        // eslint-disable-next-line no-restricted-globals
        self.FIREBASE_APPCHECK_DEBUG_TOKEN = FIREBASE_APP_CHECK_DEBUG_TOKEN
      }
    } else {
      logger.log('Environment detected: production.')
    }

    return initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_ENTERPRISE_SITE_KEY),
      isTokenAutoRefreshEnabled: true, // Set to true to allow auto-refresh.
    })
  } catch (error) {
    logger.error('App Check initialization failed:', error.message)
    logger.warn('App Check is not yet fully configured in Firebase Console. Auth flow will proceed without App Check tokens.')
    // Return a mock/placeholder to prevent downstream errors
    return null
  }
}

/**
 * @const {AppCheck} FireAppCheck
 * @desc This is the Firebase App Check instance initialized
 * with our Firebase app instance.
 */
export const FireAppCheck = getFireAppCheck(firebaseApp)

// Initialize Remote Config and get a reference to the service.
const FireRemoteConfig = getRemoteConfig(firebaseApp)

// Set minimum fetch interval to 1 hour.
// During development, it's recommended to set a relatively low minimum fetch interval.
FireRemoteConfig.settings.minimumFetchIntervalMillis = 3600

// @TODO Download a generated JSON file that includes all default RemoteConfig values and include it in our app bundle.
const FIRESTORE_COLLECTION = {
  AUTH: 'person',
  ORG: 'organization',
}

export const API_ENDPOINT = {
  SERVICES: IS_ENV_UAT ? 'client_services' : 'services',
}

// Set default config values. Remote Config will use these
// values if no value is set in the Remote Config console.
FireRemoteConfig.defaultConfig = {
  API_ENDPOINT_TIMESHEET_SERVICES: API_ENDPOINT.SERVICES,
  FIRESTORE_COLLECTION_AUTH: FIRESTORE_COLLECTION.AUTH,
  FIRESTORE_COLLECTION_ORG: FIRESTORE_COLLECTION.ORG,
}

// Fetch and activate Remote Config values.
fetchAndActivate(FireRemoteConfig).then((activated) => {
  if (activated) {
    FIRESTORE_COLLECTION.AUTH = getValue(FireRemoteConfig, 'FIRESTORE_COLLECTION_AUTH').asString()
    FIRESTORE_COLLECTION.ORG = getValue(FireRemoteConfig, 'FIRESTORE_COLLECTION_ORG').asString()

    API_ENDPOINT.SERVICES = getValue(FireRemoteConfig, 'API_ENDPOINT_TIMESHEET_SERVICES').asString()

    logger.log('Remote Config was activated')
  } else {
    logger.log('Remote Config was not activated')
  }
}).catch((error) => {
  logger.error('Remote Config Error', error)
})

// Save configured Store references.
let analytics = null

isSupported().then((isSupportedApp) => {
  if (isSupportedApp) analytics = getAnalytics(firebaseApp)
})

const Firestore = initializeFirestore(firebaseApp, {
  localCache: persistentLocalCache(/* settings */{
    tabManager: persistentMultipleTabManager(),
  }),
})

// getFirestore(firebaseApp)
export const Fireauth = getAuth(firebaseApp)

/**
 * Wait for Firebase Auth to expose a current user.
 * @param {number} timeoutMs
 * @returns {Promise<import('firebase/auth').User|null>}
 */
export const waitForAuth = (timeoutMs = 10000) => {
  if (Fireauth.currentUser) return Promise.resolve(Fireauth.currentUser)

  return new Promise((resolve) => {
    let unsubscribe = null

    const timer = setTimeout(() => {
      if (unsubscribe) unsubscribe()
      resolve(null)
    }, timeoutMs)

    unsubscribe = onAuthStateChanged(Fireauth, (user) => {
      clearTimeout(timer)
      if (unsubscribe) unsubscribe()
      resolve(user)
    })
  })
}

/**
 * Wait for App Check token availability.
 * Resolves true when token is fetched, false on timeout/error/unavailable.
 * @param {number} timeoutMs
 * @returns {Promise<boolean>}
 */
export const waitForAppCheck = (timeoutMs = 5000) => new Promise((resolve) => {
  // If App Check is not initialized (null), resolve as not ready
  // This allows auth flow to proceed without blocking on App Check
  if (!FireAppCheck) {
    logger.info('App Check not initialized - auth will proceed without App Check tokens')
    resolve(false)
    return
  }

  const timer = setTimeout(() => resolve(false), timeoutMs)

  getToken(FireAppCheck, false)
    .then(() => {
      clearTimeout(timer)
      resolve(true)
    })
    .catch(() => {
      clearTimeout(timer)
      resolve(false)
    })
})

/**
 * Wait for the Firebase auth and App Check surfaces used during auth bootstrap.
 * @param {Object} [options]
 * @param {number} [options.authTimeoutMs=10000]
 * @param {number} [options.appCheckTimeoutMs=5000]
 * @param {string|null} [options.expectedEmail=null]
 * @returns {Promise<{authUser: import('firebase/auth').User|null, appCheckReady: boolean, authMatchesExpected: boolean}>}
 */
export const waitForFirebaseReady = async ({
  authTimeoutMs = 10000,
  appCheckTimeoutMs = 5000,
  expectedEmail = null,
} = {}) => {
  const authUser = await waitForAuth(authTimeoutMs)
  const appCheckReady = await waitForAppCheck(appCheckTimeoutMs)

  const normalizedExpected = expectedEmail && expectedEmail.toLowerCase()
  const normalizedCurrent = authUser?.email && authUser.email.toLowerCase()
  const authMatchesExpected = !normalizedExpected || normalizedExpected === normalizedCurrent

  return { authUser, appCheckReady, authMatchesExpected }
}

export const setTenantIdOnFireauth = (tenantId) => {
  Fireauth.tenantId = tenantId
}

export const getTenantIdFromFireauth = () => Fireauth.tenantId

const firecollection = {
  change: () => collection(Firestore, 'change'),
  docs: () => collection(Firestore, 'app/docs/links'),
  domain: () => collection(Firestore, 'domain'),
  enterprise: () => collection(Firestore, 'enterprise'),
  message: () => collection(Firestore, 'message'),
  organization: () => collection(Firestore, 'organization'),
  person: () => collection(Firestore, 'person'),
  project: () => collection(Firestore, 'project'),
  'project-note': () => collection(Firestore, 'project-note'),
  'project-platform': () => collection(Firestore, 'project-platform'),
  'project-status': () => collection(Firestore, 'project-status'),
  'project-type': () => collection(Firestore, 'project-type'),
  provider: () => collection(Firestore, 'provider'),
  report: () => collection(Firestore, 'report'),
  error(catcher = logger.error, collectionName = 'operation') {
    return (FirebaseError) => {
      const { code, message: firebaseMessage } = FirebaseError

      const hasMissingOrInsufficientPermissions = /missing or insufficient permissions/i.test(firebaseMessage)

      // Security: Don't expose collection names or detailed permissions info to unauthenticated users
      // Instead, log full details to Sentry and show generic message to user
      const userMessage = hasMissingOrInsufficientPermissions
        ? 'Access Denied - Please sign in to continue'
        : firebaseMessage || 'Record Not Found'

      // Log detailed error info for debugging (will go to Sentry)
      const detailedMessage = hasMissingOrInsufficientPermissions
        ? `${firebaseMessage.replace('.', '')} for ${collectionName}.`
        : firebaseMessage

      const error = {
        code: code || 404,
        layer: 'app/store',
        message: userMessage,
        // Include detailed info for Sentry/logging but not shown to user
        _debugInfo: {
          collectionName,
          originalMessage: detailedMessage,
          firebaseCode: code,
        },
      }

      // Log to console in development for debugging
      if (IS_ENV_DEV) {
        logger.error('[Firestore Error]', {
          collection: collectionName,
          code,
          message: detailedMessage,
        })
      }

      return catcher({ error })
    }
  },
  /**
   * Watches documents, running observers in response to document changes.
   * @param {string} collectionName The name of the document's collection.
   * @param {string} docId The id of the document to watch, e.g. the email for a person record.
   * @param {Object<{ error, next }>} observer The functions to call when the document changes.
   * @returns {Promise<() => void>} Resolves to a function used to stop watching the document.
   */
  async watch(collectionName, docId, observer) {
    const currentDoc = doc(Firestore, collectionName, docId)
    return onSnapshot(currentDoc, observer)
  },
  async collection(collectionName, catcher) {
    const currentCollection = this[collectionName]()
    const docs = await getDocs(currentCollection).catch(this.error(catcher, collectionName))
    const results = []

    // Store responses are not real arrays; Instead, they offer a custom forEach method.
    if (docs && docs.forEach) {
      docs.forEach((document) => {
        const result = document.data()

        if (result) result.docId = document.id

        results.push(result)
      })
    }

    return results
  },
  async get(collectionName, docId, catcher) {
    const currentDoc = doc(Firestore, collectionName, docId)

    const document = await getDoc(currentDoc).catch(this.error(catcher, collectionName))
    const response = (document && !document.error && document.exists() && document.data()) || null

    if (response) {
      response.docId = document.id
    }

    return response
  },
  async set(collectionName, docId, newData, options) {
    const currentCollection = this[collectionName]()
    return setDoc(doc(currentCollection, docId), newData, options)
  },
  async delete(collectionName, docId) {
    const currentCollection = this[collectionName]()
    return deleteDoc(doc(currentCollection, docId))
  },

  orderBy(field, order = 'asc') {
    return orderBy(field, order)
  },
  where(whereClauses) {
    return whereClauses.map((clause) => {
      const [field, operator, value] = clause
      return where(field, operator, value)
    })
  },
  query(collectionName, whereClauses) {
    const currentCollection = this[collectionName]()
    const querySnapshot = query(currentCollection, ...whereClauses)
    const docs = getDocs(querySnapshot).catch(this.error(logger.error, collectionName))
    const results = []

    // Store responses are not real arrays; Instead, they offer a custom forEach method.
    if (docs && docs.forEach) {
      docs.forEach((document) => {
        const result = document.data()

        if (result) result.docId = document.id

        results.push(result)
      })
    }

    return results
  },
  async findAll(collectionName, whereClauses, limitCount = 100, sortOrder, catcher) {
    const currentCollection = this[collectionName]()
    const querySnapshot = query(currentCollection, ...whereClauses, limit(limitCount), sortOrder)
    const docs = await getDocs(querySnapshot).catch(this.error(catcher, collectionName))
    const results = []

    // Store responses are not real arrays; Instead, they offer a custom forEach method.
    if (docs && docs.forEach) {
      docs.forEach((document) => {
        const result = document.data()

        if (result) result.docId = document.id

        results.push(result)
      })
    }

    return results
  },

  auth(docId, catcher) {
    if (docId) {
      return this.get(FIRESTORE_COLLECTION.AUTH, docId, catcher)
    }

    return this.collection(FIRESTORE_COLLECTION.AUTH, catcher)
  },

  org(docId, catcher) {
    if (docId) {
      return this.get(FIRESTORE_COLLECTION.ORG, docId, catcher)
    }

    return this.collection(FIRESTORE_COLLECTION.ORG, catcher)
  },
}

const fireanalytics = {
  event(eventName, eventData) {
    if (analytics) {
      logEvent(analytics, eventName, eventData)
    }
  },
  async setUser(analyticsData) {
    const { analyticsUser, personId } = analyticsData

    setUserId(analytics, personId)

    return setUserProperties(analytics, analyticsUser)
  },
}

const providerMap = {
  google({ isInternalDomain = false }) {
    const identityProvider = new GoogleAuthProvider()

    // Base scopes for all users
    identityProvider.addScope('email')
    identityProvider.addScope('profile')

    // Add Google Sheets API scopes for @adops.com users only
    // This allows project sync functionality for internal admins
    if (isInternalDomain) {
      identityProvider.addScope('https://www.googleapis.com/auth/spreadsheets.readonly')
      identityProvider.addScope('https://www.googleapis.com/auth/drive.readonly')
    }

    identityProvider.setCustomParameters({ prompt: 'select_account' })

    return identityProvider
  },
  link() {
    return new EmailAuthProvider()
  },
  password() {
    return new EmailAuthProvider()
  },
  saml({ providerId }) {
    return new SAMLAuthProvider(providerId)
  },
}

const fireauth = {
  getCurrentUser() {
    return Fireauth.currentUser
  },

  onAuthStateChanged(fnSuccess, fnError) {
    return onAuthStateChanged(Fireauth, fnSuccess, fnError)
  },

  sendEmailVerification(url) {
    return sendEmailVerification(Fireauth.currentUser, {
      url,
      handleCodeInApp: true,
    })
  },

  sendSignInLinkToEmail(email, url) {
    return sendSignInLinkToEmail(Fireauth, email, {
      url,
      handleCodeInApp: true,
    })
  },

  isSignInWithEmailLink(email) {
    return isSignInWithEmailLink(Fireauth, email)
  },

  signInWithEmailLink(email, url) {
    return signInWithEmailLink(Fireauth, email, url)
  },

  signInWithCredential(credential) {
    return signInWithCredential(Fireauth, credential)
  },

  getGoogleCredentialFromError(error) {
    return GoogleAuthProvider.credentialFromError(error)
  },

  linkWithCredential(credential) {
    return linkWithCredential(Fireauth.currentUser, credential)
  },

  reauthenticateWithCredential(credential) {
    return reauthenticateWithCredential(Fireauth.currentUser, credential)
  },

  createUserWithEmailAndPassword(email, password) {
    return createUserWithEmailAndPassword(Fireauth, email, password)
  },

  updatePassword(password) {
    return updatePassword(Fireauth.currentUser, password)
  },

  signInWithPassword(email, password) {
    return signInWithEmailAndPassword(Fireauth, email, password)
  },

  /**
   * @function signInWithProvider
   * @desc Signs a user in with the given provider.
   * @param {string} providerDocument The provider's Firestore document.
   * @param {string} providerDocument.providerId The provider ID to use for sign-in.
   * @param {string} providerDocument.providerType The type of provider to use for sign-in.
   * @param {boolean} isInternalDomain Whether the sign-in is for an internal domain user.
   * @return {Promise<UserCredential>}
   */
  signInWithProvider({ providerId, providerType }, isInternalDomain = false) {
    const foundProvider = providerMap[providerType] !== undefined

    // Validate provider type.
    if (!foundProvider) {
      throw new Error(`Unsupported provider type: ${providerType}`)
    }

    // Initialize provider.
    const provider = providerMap[providerType]({ isInternalDomain, providerId })

    // Validate provider initialization.
    if (!provider) {
      throw new Error('Provider failed initialization')
    }

    // Handle password provider separately as it requires a different
    // sign-in method.
    if (providerType === 'password') {
      console.log('Signing in with email and password using Password provider.', provider)

      const { email, password } = providerId

      // Sign in with email and password.
      return signInWithEmailAndPassword(Fireauth, email, password)
    }

    console.log(`Signing in with ${providerType} provider using Popup method.`)

    // Prefer popup flow, but fall back to redirect when the browser blocks or
    // cancels the popup before auth completes.
    return signInWithPopup(Fireauth, provider).catch((error) => {
      if (error.code === 'auth/popup-blocked'
        || error.code === 'auth/popup-closed-by-user'
        || error.code === 'auth/cancelled-popup-request'
        || error.message?.includes('popup')
        || error.message?.includes('blocked')
        || error.message?.includes('closed')) {
        logger.warn('[Auth] Popup sign-in unavailable, falling back to redirect sign-in.', {
          code: error.code,
          message: error.message,
          providerType,
        })

        return signInWithRedirect(Fireauth, provider)
      }

      // Re-throw other errors
      throw error
    })
  },

  signOut() {
    return signOut(Fireauth)
  },

  // Note: getRedirectResult() removed - incompatible with COOP headers
  // Redirect auth flow doesn't work with Cross-Origin-Opener-Policy: same-origin-allow-popups

  getIdToken() {
    return Fireauth.currentUser.getIdToken()
  },

  async getFirebaseAuthUser(email) {
    return Fireauth.getUserByEmail(email)
  },
}

export {
  fireauth,
  fireanalytics,
  firecollection,
  GoogleAuthProvider,
  Timestamp,
}
